/**
 * dateUtils.js — Centralised date formatting for Cryptcrawler
 *
 * All functions treat input as UTC and display in the user's local timezone.
 * Handles ISO strings (with Z or +00:00), plain date strings, and Date objects.
 */

/**
 * Normalise any timestamp input to a Date object.
 * Returns null if the input is falsy or unparseable.
 */
function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    // Unix seconds (< year 3000 in ms would be > 32503680000)
    const ms = value < 1e12 ? value * 1000 : value;
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'string') {
    // Normalise Python isoformat: replace +00:00 suffix with Z
    let s = value.trim();
    if (s.endsWith('+00:00')) s = s.slice(0, -6) + 'Z';
    // If no timezone info at all, assume UTC
    if (!s.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(s)) s = s + 'Z';
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * Full date + time, e.g. "15 Mar 2024, 10:22:31"
 */
export function formatDate(value) {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleString(undefined, {
    day:    '2-digit',
    month:  'short',
    year:   'numeric',
    hour:   '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * Short date only, e.g. "15 Mar 2024"
 */
export function formatDateShort(value) {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleDateString(undefined, {
    day:   '2-digit',
    month: 'short',
    year:  'numeric',
  });
}

/**
 * Relative time, e.g. "3 days ago", "just now"
 */
export function formatDateRelative(value) {
  const d = toDate(value);
  if (!d) return '—';
  const diffMs  = Date.now() - d.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec <  60)  return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} hr ago`;
  if (diffSec < 86400 * 30) return `${Math.floor(diffSec / 86400)} days ago`;
  return formatDateShort(d);
}

/**
 * Compact graph label, e.g. "2024-03-15 10:22"
 */
export function formatDateGraph(value) {
  const d = toDate(value);
  if (!d) return '';
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const dd   = String(d.getDate()).padStart(2, '0');
  const hh   = String(d.getHours()).padStart(2, '0');
  const min  = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
}
